import { useState } from 'react';

import { AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';

import instance from 'configs/requests';
import { ToastNotificationContent } from 'components/UI';
import { CheckIcon, CloseIcon, SpinnerIcon } from 'assets/icons';
import { generateCSVContent, downloadCSV, ICsvColumn } from 'utils/CsvUtils';

export interface IExportCSVConfig<T> {
  filename: string;
  fetchData: () => Promise<T[]>;
  columns: ICsvColumn[];
  extractRowValues: (row: T) => Record<string, string>;
}

const useExportCSV = () => {
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = async <T extends object>({
    filename,
    fetchData,
    columns,
    extractRowValues
  }: IExportCSVConfig<T>) => {
    if (isExporting) return;

    setIsExporting(true);
    const toastId = toast(
      <ToastNotificationContent
        icon={<SpinnerIcon className="icon" />}
        text="csvExportGeneratingKey"
      />,
      { autoClose: false, closeOnClick: false, draggable: false }
    );

    try {
      const rows = await fetchData();
      const csvRows = rows.map(extractRowValues);
      const content = generateCSVContent(columns, csvRows);
      downloadCSV(content, filename);
      toast.dismiss(toastId);
      toast(
        <ToastNotificationContent
          icon={<CheckIcon className="icon" />}
          text="csvExportSuccessKey"
        />
      );
    } catch {
      toast.dismiss(toastId);
      toast.error(
        <ToastNotificationContent icon={<CloseIcon className="icon" />} text="csvExportErrorKey" />
      );
    } finally {
      setIsExporting(false);
    }
  };

  const exportCSVByUrl = async (config: AxiosRequestConfig) => {
    if (isExporting) return;

    setIsExporting(true);
    const toastId = toast(
      <ToastNotificationContent
        icon={<SpinnerIcon className="icon" />}
        text="csvExportGeneratingKey"
      />,
      { autoClose: false, closeOnClick: false, draggable: false }
    );

    try {
      const { data } = await instance.request<{ url: string }>(config);
      window.location.href = data.url;
      toast.dismiss(toastId);
      toast(
        <ToastNotificationContent
          icon={<CheckIcon className="icon" />}
          text="csvExportSuccessKey"
        />
      );
    } catch {
      toast.dismiss(toastId);
      toast.error(
        <ToastNotificationContent icon={<CloseIcon className="icon" />} text="csvExportErrorKey" />
      );
    } finally {
      setIsExporting(false);
    }
  };

  return { exportCSV, exportCSVByUrl, isExporting };
};

export default useExportCSV;
